import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Shield,
  ShieldOff,
  Trash2,
  UserCog,
  UserPlus,
  Users,
} from 'lucide-react'

import {
  deleteDepartment,
  getCollaborationEligibility,
  getProduction,
  listDepartments,
  listMembers,
  listProductionAssignments,
  listProductionInvites,
  patchMember,
  removeMember,
  revokeProductionInvite,
  type CollaborationEligibility,
  type Production,
  type ProductionDepartment,
  type ProductionMember,
  type ProductionPendingInvite,
} from '@/api/studio-production'
import { cn } from '@/shared/lib/utils'
import { CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { PageHeading } from '@/shared/components/PageHeading'
import { InviteMemberModal, inviteAbsoluteUrl, memberDisplayName, memberInitials } from '@/features/studio/modals/InviteMemberModal'
import { EditMemberModal } from '@/features/studio/modals/EditMemberModal'
import { DepartmentModal } from '@/features/studio/modals/DepartmentModal'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { useAuth } from '@/hooks'
import {
  PRODUCTION_BORDER_SUBTLE,
  PRODUCTION_TABLE_HEAD_ALT_CLASS,
  PRODUCTION_TABLE_ROW_CLASS,
  PRODUCTION_TABLE_ROW_HOVER_CLASS,
} from '@/features/studio/lib/production-shell'
import { ProtoAvatar, ProtoBadge, ProtoBtn, ProtoListTable, ProtoPagination, ProtoTabs } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { ProductionBudgetPanel } from '@/features/studio/modals/ProductionBudgetPanel'
import { ProductionAssignmentsPanel } from '@/features/studio/modals/ProductionAssignmentsPanel'
import { aggregateQuotaByAssignee } from '@/features/studio/lib/assignment-display'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

const UNASSIGNED_KEY = '__none__'
const MEMBER_PAGE_SIZE = 20

type DeptTreeNode = {
  key: string
  department: ProductionDepartment | null
  label: string
  members: ProductionMember[]
}

export function TeamPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const { user } = useAuth()
  const [members, setMembers] = useState<ProductionMember[]>([])
  const [memberTotal, setMemberTotal] = useState(0)
  const [memberPage, setMemberPage] = useState(0)
  const [allMembers, setAllMembers] = useState<ProductionMember[]>([])
  const [departments, setDepartments] = useState<ProductionDepartment[]>([])
  const [pendingInvites, setPendingInvites] = useState<ProductionPendingInvite[]>([])
  const [eligibility, setEligibility] = useState<CollaborationEligibility | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteDefaultDeptId, setInviteDefaultDeptId] = useState('')
  const [editMember, setEditMember] = useState<ProductionMember | null>(null)
  const [removeMemberId, setRemoveMemberId] = useState<number | null>(null)
  const [deleteDeptId, setDeleteDeptId] = useState<string | null>(null)
  const [revokeInviteId, setRevokeInviteId] = useState<number | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [busyUserId, setBusyUserId] = useState<number | null>(null)
  const [deptModalOpen, setDeptModalOpen] = useState(false)
  const [deptModalMode, setDeptModalMode] = useState<'create' | 'edit'>('create')
  const [editingDepartment, setEditingDepartment] = useState<ProductionDepartment | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [, setProduction] = useState<Production | null>(null)
  const [quotaByUser, setQuotaByUser] = useState(
    new Map<number, { remaining: number; allocated: number; assignmentCount: number; itemCount: number }>(),
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab')
  const [tab, setTab] = useState<'members' | 'budget' | 'assignments'>(() =>
    tabFromUrl === 'budget' || tabFromUrl === 'assignments' ? tabFromUrl : 'members',
  )

  useEffect(() => {
    if (tabFromUrl === 'budget' || tabFromUrl === 'assignments') setTab(tabFromUrl)
    else setTab('members')
  }, [tabFromUrl])

  const myMember = useMemo(
    () => allMembers.find((m) => m.user_id === user?.id) ?? members.find((m) => m.user_id === user?.id),
    [allMembers, members, user?.id],
  )
  const assignGate = useMemo(
    () => ({
      isOwner,
      isDeptAdmin: !!myMember?.is_department_admin,
      myDepartmentId: myMember?.department_id ?? null,
    }),
    [isOwner, myMember],
  )

  const reloadMembers = useCallback(async () => {
    const [memRes, allRes] = await Promise.all([
      listMembers(productionId, { limit: MEMBER_PAGE_SIZE, offset: memberPage * MEMBER_PAGE_SIZE }),
      listMembers(productionId, { limit: 500, offset: 0 }),
    ])
    setMembers(memRes.items)
    setMemberTotal(memRes.total)
    setAllMembers(allRes.items)
  }, [productionId, memberPage])

  const reloadQuota = useCallback(async () => {
    const assignRes = await listProductionAssignments(productionId, undefined, { limit: 500, offset: 0 }).catch(
      () => ({ items: [] }),
    )
    setQuotaByUser(aggregateQuotaByAssignee(assignRes.items))
  }, [productionId])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [deptRes, elig, prod] = await Promise.all([
        listDepartments(productionId).catch(() => ({ items: [] as ProductionDepartment[] })),
        getCollaborationEligibility(productionId).catch(() => null),
        getProduction(productionId).catch(() => null),
      ])
      setDepartments(deptRes.items ?? [])
      setEligibility(elig)
      setProduction(prod)
      const owner =
        !!prod?.is_owner || (prod?.user_id != null && user?.id != null && prod.user_id === user.id)
      setIsOwner(owner)
      await reloadMembers()
      await reloadQuota()
      if (owner) {
        const inv = await listProductionInvites(productionId, { status: 'pending' }).catch(() => ({ items: [] }))
        setPendingInvites(inv.items)
      } else {
        setPendingInvites([])
      }
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, user?.id, reloadMembers, reloadQuota])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (loading) return
    void reloadMembers().catch(() => {})
  }, [memberPage, loading, reloadMembers])

  useEffect(() => {
    const onFocus = () => {
      void reload()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [reload])

  async function openInviteModal(departmentId = '') {
    setInviteDefaultDeptId(departmentId)
    setInviteOpen(true)
    try {
      const elig = await getCollaborationEligibility(productionId)
      setEligibility(elig)
    } catch {
      /* keep prior eligibility */
    }
  }

  function openCreateDepartmentModal() {
    setDeptModalMode('create')
    setEditingDepartment(null)
    setDeptModalOpen(true)
  }

  function openEditDepartmentModal(dept: ProductionDepartment) {
    setDeptModalMode('edit')
    setEditingDepartment(dept)
    setDeptModalOpen(true)
  }

  async function handleToggleDeptAdmin(member: ProductionMember) {
    if (!isOwner) return
    setBusyUserId(member.user_id)
    setError(null)
    try {
      await patchMember(productionId, member.user_id, {
        is_department_admin: !member.is_department_admin,
      })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.patchMemberFailed'))
    } finally {
      setBusyUserId(null)
    }
  }

  async function confirmDeleteDepartment() {
    if (!deleteDeptId) return
    setConfirmBusy(true)
    try {
      await deleteDepartment(productionId, deleteDeptId)
      setDeleteDeptId(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.deptDeleteFailed'))
    } finally {
      setConfirmBusy(false)
    }
  }

  async function confirmRemoveMember() {
    if (removeMemberId == null) return
    setConfirmBusy(true)
    try {
      await removeMember(productionId, removeMemberId)
      setRemoveMemberId(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.removeFailed'))
    } finally {
      setConfirmBusy(false)
    }
  }

  async function confirmRevokeInvite() {
    if (revokeInviteId == null) return
    setConfirmBusy(true)
    try {
      await revokeProductionInvite(productionId, revokeInviteId)
      setRevokeInviteId(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.inviteRevokeFailed'))
    } finally {
      setConfirmBusy(false)
    }
  }

  const tasksPath = `${productionBasePath(productionId)}/tasks`
  const canInvite = eligibility?.can_invite ?? false
  const hasCollab = eligibility?.has_studio_collaboration ?? false
  const seatsUsed = eligibility?.collaboration_seats_used ?? allMembers.length
  const seatsTotal = hasCollab ? eligibility?.collaboration_seats ?? 0 : 0

  const memberCountByDept = useMemo(() => {
    const map = new Map<string, number>()
    for (const m of allMembers) {
      const key = m.department_id && departments.some((d) => d.id === m.department_id) ? m.department_id : UNASSIGNED_KEY
      map.set(key, (map.get(key) ?? 0) + 1)
    }
    return map
  }, [allMembers, departments])

  const deptTree = useMemo(() => {
    const byDept = new Map<string, ProductionMember[]>()
    const unassigned: ProductionMember[] = []
    const knownDeptIds = new Set(departments.map((d) => d.id))

    for (const m of members) {
      const deptId = m.department_id ?? ''
      if (!deptId || !knownDeptIds.has(deptId)) {
        unassigned.push(m)
        continue
      }
      const list = byDept.get(deptId) ?? []
      list.push(m)
      byDept.set(deptId, list)
    }

    const nodes: DeptTreeNode[] = departments.map((d) => ({
      key: d.id,
      department: d,
      label: d.name,
      members: byDept.get(d.id) ?? [],
    }))

    if (unassigned.length > 0 || (departments.length === 0 && members.length > 0)) {
      nodes.push({
        key: UNASSIGNED_KEY,
        department: null,
        label: t('team.noDepartmentGroup'),
        members: unassigned,
      })
    }

    return nodes.filter((n) => n.members.length > 0 || n.department != null)
  }, [members, departments, t])

  useEffect(() => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      for (const node of deptTree) {
        next.add(node.key)
      }
      return next
    })
  }, [deptTree])

  function toggleExpanded(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function renderDeptActions(dept: ProductionDepartment) {
    if (!isOwner) return null
    const memberCount = memberCountByDept.get(dept.id) ?? 0
    return (
      <div className="flex flex-wrap justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {canInvite ? (
          <ProtoBtn size="sm" variant="ghost" onClick={() => void openInviteModal(dept.id)}>
            <UserPlus className="h-3.5 w-3.5" />
            {t('team.inviteToDept')}
          </ProtoBtn>
        ) : null}
        <ProtoBtn size="sm" variant="ghost" onClick={() => openEditDepartmentModal(dept)}>
          <Pencil className="h-3.5 w-3.5" />
          {t('team.editDept')}
        </ProtoBtn>
        <ProtoBtn
          size="sm"
          variant="danger"
          disabled={memberCount > 0}
          title={memberCount > 0 ? t('team.deptDeleteBlocked') : undefined}
          onClick={() => setDeleteDeptId(dept.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ProtoBtn>
      </div>
    )
  }

  function renderMemberActions(m: ProductionMember) {
    const canManage = isOwner && m.user_id !== user?.id
    if (!canManage) return <span className="text-zinc-600">—</span>
    return (
      <div className="flex flex-wrap justify-end gap-1">
        <ProtoBtn size="sm" variant="ghost" onClick={() => setEditMember(m)}>
          <UserCog className="h-3.5 w-3.5" />
          {t('team.editMember')}
        </ProtoBtn>
        <ProtoBtn
          size="sm"
          variant="ghost"
          disabled={busyUserId === m.user_id}
          onClick={() => void handleToggleDeptAdmin(m)}
        >
          {m.is_department_admin ? (
            <>
              <ShieldOff className="h-3.5 w-3.5" />
              {t('team.revokeDeptAdmin')}
            </>
          ) : (
            <>
              <Shield className="h-3.5 w-3.5" />
              {t('team.makeDeptAdmin')}
            </>
          )}
        </ProtoBtn>
        <ProtoBtn
          size="sm"
          variant="danger"
          disabled={busyUserId === m.user_id}
          onClick={() => setRemoveMemberId(m.user_id)}
        >
          {busyUserId === m.user_id ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            t('team.remove')
          )}
        </ProtoBtn>
      </div>
    )
  }

  return (
    <CatalogListPageShell>
      <PageHeading icon={Users} tone="indigo" title={t('team.title')}>
        <div className="flex flex-col items-end gap-2">
          {hasCollab ? (
            <div className={cn('rounded-full border bg-amber-500/10 px-3 py-1 text-xs text-amber-200', PRODUCTION_BORDER_SUBTLE)}>
              {t('team.seats', { used: seatsUsed, total: seatsTotal })}
            </div>
          ) : (
            <div className="rounded-full border border-zinc-600/40 bg-zinc-800/40 px-3 py-1 text-xs text-zinc-400">
              {t('team.personalMode')}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {isOwner ? (
              <ProtoBtn size="sm" variant="secondary" onClick={openCreateDepartmentModal}>
                <Plus className="h-4 w-4" />
                {t('team.deptCreate')}
              </ProtoBtn>
            ) : null}
            <CatalogListPrimaryButton onClick={() => void openInviteModal()}>
              <UserPlus className="h-4 w-4" />
              {t('team.invite')}
            </CatalogListPrimaryButton>
          </div>
        </div>
      </PageHeading>

      {error ? <p className="mb-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      <div className="mb-6">
        <ProtoTabs
          tabs={[
            { id: 'members', label: t('team.tabMembers') },
            { id: 'budget', label: t('team.tabBudget') },
            { id: 'assignments', label: t('team.tabAssignments') },
          ]}
          active={tab}
          onChange={(id) => {
            const next = id as 'members' | 'budget' | 'assignments'
            setTab(next)
            const nextParams = new URLSearchParams(searchParams)
            if (next === 'members') nextParams.delete('tab')
            else nextParams.set('tab', next)
            setSearchParams(nextParams, { replace: true })
          }}
        />
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
        </div>
      ) : tab === 'members' ? (
        <div className="space-y-6">
          <ProtoListTable production>
            <table className="min-w-full text-left text-sm">
              <thead className={cn(PRODUCTION_TABLE_HEAD_ALT_CLASS, 'text-xs text-zinc-500')}>
                <tr>
                  <th className="px-4 py-3">{t('team.colMember')}</th>
                  <th className="px-4 py-3">{t('team.colRole')}</th>
                  <th className="px-4 py-3">{t('team.colQuota')}</th>
                  <th className="px-4 py-3 text-right">{t('team.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {deptTree.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-xs text-zinc-500">
                      {t('team.membersEmpty')}
                    </td>
                  </tr>
                ) : (
                  deptTree.map((node) => {
                    const expanded = expandedKeys.has(node.key)
                    const isUnassigned = node.key === UNASSIGNED_KEY
                    const deptMemberTotal = isUnassigned
                      ? memberCountByDept.get(UNASSIGNED_KEY) ?? node.members.length
                      : memberCountByDept.get(node.key) ?? node.members.length
                    return (
                      <Fragment key={node.key}>
                        <tr className={cn(PRODUCTION_TABLE_ROW_CLASS, 'bg-white/[0.02] text-zinc-400')}>
                          <td colSpan={3} className="px-4 py-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-auto w-full justify-start gap-2 text-left text-xs font-medium"
                              onClick={() => toggleExpanded(node.key)}
                            >
                              {expanded ? (
                                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                              )}
                              <Building2
                                className={`h-3.5 w-3.5 shrink-0 ${isUnassigned ? 'text-zinc-500' : 'text-indigo-400'}`}
                              />
                              <span className="text-zinc-300">{node.label}</span>
                              <span className="text-zinc-600">
                                {t('team.deptMemberCount', { count: deptMemberTotal })}
                              </span>
                            </Button>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {node.department ? renderDeptActions(node.department) : null}
                          </td>
                        </tr>
                        {expanded && node.members.length === 0 ? (
                          <tr className={PRODUCTION_TABLE_ROW_CLASS}>
                            <td colSpan={4} className="px-4 py-3 pl-10 text-xs text-zinc-600">
                              {t('team.deptMembersEmpty')}
                            </td>
                          </tr>
                        ) : null}
                        {expanded
                          ? node.members.map((m) => {
                              const q = quotaByUser.get(m.user_id)
                              const remaining = q?.remaining ?? 0
                              return (
                                <tr key={m.user_id} className={PRODUCTION_TABLE_ROW_HOVER_CLASS}>
                                  <td className="px-4 py-3 pl-10">
                                    <div className="flex items-center gap-3">
                                      <ProtoAvatar label={memberInitials(m)} />
                                      <div>
                                        <p className="text-sm text-zinc-200">
                                          {memberDisplayName(m)}
                                          {m.is_department_admin ? (
                                            <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-200">
                                              <Shield className="h-2.5 w-2.5" />
                                              {t('team.deptAdmin')}
                                            </span>
                                          ) : null}
                                        </p>
                                        <p className="text-[11px] text-zinc-600">#{m.user_id}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <ProtoBadge tone="default">
                                      {t(`production.roles.${m.role}`, { defaultValue: m.role })}
                                    </ProtoBadge>
                                  </td>
                                  <td className="px-4 py-3">
                                    <ProtoBadge tone={remaining > 0 ? 'amber' : 'default'}>
                                      {t('team.memberQuotaBadge', { n: remaining })}
                                    </ProtoBadge>
                                  </td>
                                  <td className="px-4 py-3">{renderMemberActions(m)}</td>
                                </tr>
                              )
                            })
                          : null}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </ProtoListTable>

          <ProtoPagination
            page={memberPage}
            pageSize={MEMBER_PAGE_SIZE}
            total={memberTotal}
            onPageChange={setMemberPage}
            prevLabel={t('common.prevPage')}
            nextLabel={t('common.nextPage')}
          />

          {isOwner && pendingInvites.length > 0 ? (
            <ProtoListTable production>
              <table className="min-w-full text-left text-sm">
                <thead className={cn(PRODUCTION_TABLE_HEAD_ALT_CLASS, 'text-xs text-zinc-500')}>
                  <tr>
                    <th className="px-4 py-3">{t('team.pendingInvitesTitle')}</th>
                    <th className="px-4 py-3">{t('team.colRole')}</th>
                    <th className="px-4 py-3 text-right">{t('team.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv) => (
                    <tr key={inv.id} className={PRODUCTION_TABLE_ROW_HOVER_CLASS}>
                      <td className="px-4 py-3 text-zinc-200">
                        {inv.email ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 text-zinc-500" />
                            {inv.email}
                          </span>
                        ) : (
                          t('team.inviteLinkOnly')
                        )}
                        <p className="mt-0.5 text-[11px] text-zinc-600">
                          {t('invite.expires', { date: new Date(inv.expires_at).toLocaleString() })}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-zinc-500">
                        {t(`production.roles.${inv.role}`, { defaultValue: inv.role })}
                        {inv.department_name ? ` · ${inv.department_name}` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap justify-end gap-1">
                          <ProtoBtn
                            size="sm"
                            variant="secondary"
                            onClick={() => void navigator.clipboard.writeText(inviteAbsoluteUrl(inv.invite_url))}
                          >
                            {t('team.copyLink')}
                          </ProtoBtn>
                          <ProtoBtn
                            size="sm"
                            variant="danger"
                            onClick={() => setRevokeInviteId(inv.id)}
                          >
                            {t('team.revokeInvite')}
                          </ProtoBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProtoListTable>
          ) : null}
        </div>
      ) : tab === 'budget' ? (
        <ProductionBudgetPanel productionId={productionId} isOwner={isOwner} departments={departments} />
      ) : (
        <ProductionAssignmentsPanel
          productionId={productionId}
          members={allMembers}
          departments={departments}
          tasksPath={tasksPath}
          gate={assignGate}
          onChanged={() => void reloadQuota()}
        />
      )}

      <DepartmentModal
        open={deptModalOpen}
        onClose={() => setDeptModalOpen(false)}
        productionId={productionId}
        mode={deptModalMode}
        department={editingDepartment}
        onSaved={() => void reload()}
      />

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false)
          setInviteDefaultDeptId('')
        }}
        productionId={productionId}
        canInvite={canInvite}
        hasCollab={hasCollab}
        departments={departments}
        defaultDepartmentId={inviteDefaultDeptId}
        onInvited={() => void reload()}
      />

      <EditMemberModal
        open={editMember != null}
        onClose={() => setEditMember(null)}
        productionId={productionId}
        member={editMember}
        departments={departments}
        onSaved={() => void reload()}
      />

      <ConfirmDialog
        open={deleteDeptId != null}
        title={t('team.deptDeleteTitle')}
        message={t('team.deptDeleteConfirm')}
        icon={Trash2}
        destructive
        busy={confirmBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDeleteDepartment()}
        onCancel={() => {
          if (confirmBusy) return
          setDeleteDeptId(null)
        }}
      />

      <ConfirmDialog
        open={removeMemberId != null}
        title={t('team.removeTitle')}
        message={t('team.removeConfirm')}
        icon={Trash2}
        destructive
        busy={confirmBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmRemoveMember()}
        onCancel={() => {
          if (confirmBusy) return
          setRemoveMemberId(null)
        }}
      />

      <ConfirmDialog
        open={revokeInviteId != null}
        title={t('team.revokeInviteTitle')}
        message={t('team.revokeInviteConfirm')}
        icon={Trash2}
        destructive
        busy={confirmBusy}
        confirmLabel={t('team.revokeInvite')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmRevokeInvite()}
        onCancel={() => {
          if (confirmBusy) return
          setRevokeInviteId(null)
        }}
      />
    </CatalogListPageShell>
  )
}
